// src/pages/assignment/SupervisorDetailPage.jsx (UPDATED)
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, X, MapPin, Activity, Package, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import AssignmentModal from '../../components/assignment/AssignmentModal';
import fieldSupervisorService from '../../services/fieldSupervisorService';

const SupervisorDetailPage = ({ supervisorId, onBack }) => {
  const [supervisorDetail, setSupervisorDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  useEffect(() => {
    fetchSupervisorDetail();
  }, [supervisorId]);

  const fetchSupervisorDetail = async () => {
    setIsLoading(true);
    try {
      const data = await fieldSupervisorService.getSupervisorDetail(supervisorId);
      setSupervisorDetail(data);
    } catch (error) {
      console.error('Error fetching supervisor detail:', error);
      toast.error('Failed to load supervisor detail');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassignById = async (assignmentId, targetName) => {
    if (window.confirm(`Are you sure you want to cancel this assignment for ${targetName}?`)) {
      try {
        await fieldSupervisorService.unassignById(assignmentId);
        toast.success('Assignment cancelled successfully!');
        fetchSupervisorDetail();
      } catch (error) {
        console.error('Error cancelling assignment:', error);
      }
    }
  };

  const handleRemoveScope = async (assignmentId, scope, targetName) => {
    if (window.confirm(`Remove ${scope} scope from ${targetName}?`)) {
      try {
        await fieldSupervisorService.removeScopeFromAssignment(assignmentId, scope);
        toast.success('Scope removed successfully!');
        fetchSupervisorDetail();
      } catch (error) {
        console.error('Error removing scope:', error);
      }
    }
  };

  const handleAssignmentComplete = () => {
    setShowAssignmentModal(false);
    fetchSupervisorDetail();
  };

  // ✅ UPDATED: Scope configuration with type-specific mapping
  const SCOPE_CONFIG = {
    // FO Segment Scopes
    'SURVEY': { label: 'Survey', icon: '🔍', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    'PERMIT': { label: 'Permit', icon: '📋', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    'IMPLEMENTATION': { label: 'Implementation', icon: '🏗️', color: 'bg-green-100 text-green-800 border-green-300' },
    
    // Tower Site Scopes
    'SITAC': { label: 'SITAC', icon: '📍', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    'CME_IMPLEMENTATION': { label: 'CME Implementation', icon: '🏗️', color: 'bg-green-100 text-green-800 border-green-300' }
  };

  const getScopeConfig = (scope) => {
    return SCOPE_CONFIG[scope] || { 
      label: scope, 
      icon: '•', 
      color: 'bg-gray-100 text-gray-800 border-gray-300' 
    };
  };

  if (isLoading || !supervisorDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Supervisor Detail</h1>
              <p className="text-gray-600 mt-1">View supervisor information and assignments</p>
            </div>
          </div>
          <button
            onClick={() => setShowAssignmentModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Assign Segment/Site
          </button>
        </div>
      </div>

      {/* Supervisor Profile Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-500 rounded-t-lg">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
              <span className="text-3xl font-bold text-blue-600">
                {supervisorDetail.fullName.charAt(0)}
              </span>
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold">{supervisorDetail.fullName}</h2>
              <p className="text-blue-100 mt-1">@{supervisorDetail.username}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  {supervisorDetail.role}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  supervisorDetail.status === 'ACTIVE' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-500 text-white'
                }`}>
                  {supervisorDetail.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Email</label>
              <p className="text-base text-gray-900">{supervisorDetail.email}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Phone</label>
              <p className="text-base text-gray-900">{supervisorDetail.phone || '-'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Last Login</label>
              <p className="text-base text-gray-900">{supervisorDetail.lastLoginAt || 'Never'}</p>
            </div>
          </div>
        </div>

        {/* Assignment Summary */}
        <div className="border-t border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Assignment Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-600">FO Segments</p>
                  <p className="text-3xl font-bold text-blue-800 mt-2">{supervisorDetail.totalSegments}</p>
                </div>
                <Package className="w-12 h-12 text-blue-600 opacity-50" />
              </div>
            </div>
            <div className="p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-red-600">Tower Sites</p>
                  <p className="text-3xl font-bold text-red-800 mt-2">{supervisorDetail.totalSites}</p>
                </div>
                <Building2 className="w-12 h-12 text-red-600 opacity-50" />
              </div>
            </div>
            <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-purple-600">Total Assignments</p>
                  <p className="text-3xl font-bold text-purple-800 mt-2">{supervisorDetail.totalAssignments}</p>
                </div>
                <Activity className="w-12 h-12 text-purple-600 opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FO Segments Section */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-500 rounded-t-lg">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Package className="w-6 h-6" />
            Assigned FO Segments ({supervisorDetail.assignedSegments.length})
          </h2>
        </div>
        <div className="p-6">
          {supervisorDetail.assignedSegments.length > 0 ? (
            <div className="space-y-4">
              {supervisorDetail.assignedSegments.map((segment) => (
                <div key={segment.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {segment.spanId}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                          {segment.ringType}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          segment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          segment.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {segment.status}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{segment.siteNameAB}</h3>

                      {/* Assignments with Scopes */}
                      {segment.assignments && segment.assignments.length > 0 && (
                        <div className="mb-3 space-y-2">
                          <p className="text-sm font-semibold text-gray-700">Assigned Scopes:</p>
                          {segment.assignments.map((assignment) => (
                            <div key={assignment.assignmentId} className="flex items-center gap-2 flex-wrap">
                              {assignment.scopes.map((scope) => {
                                const config = getScopeConfig(scope);
                                return (
                                  <div key={scope} className="group relative">
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
                                      <span>{config.icon}</span>
                                      <span>{config.label}</span>
                                      {assignment.scopes.length > 1 && (
                                        <button
                                          onClick={() => handleRemoveScope(assignment.assignmentId, scope, segment.siteNameAB)}
                                          className="ml-1 hover:bg-red-200 rounded-full p-0.5 transition-colors"
                                          title="Remove this scope"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                        <div>
                          <span className="text-gray-500">Project:</span>
                          <p className="font-medium text-gray-900">{segment.projectName || '-'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Province:</span>
                          <p className="font-medium text-gray-900">{segment.mainArea}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Kota:</span>
                          <p className="font-medium text-gray-900">{segment.kota}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Distance:</span>
                          <p className="font-medium text-gray-900">{segment.commercialDistanceKm} km</p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-semibold text-gray-900">{segment.progressPercentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-600 to-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${segment.progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Unassign Button */}
                    {segment.assignments && segment.assignments.length > 0 && (
                      <button
                        onClick={() => handleUnassignById(segment.assignments[0].assignmentId, segment.siteNameAB)}
                        className="ml-4 text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel Assignment"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No FO Segments assigned yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Tower Sites Section */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 bg-gradient-to-r from-red-600 to-red-500 rounded-t-lg">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Assigned Tower Sites ({supervisorDetail.assignedSites.length})
          </h2>
        </div>
        <div className="p-6">
          {supervisorDetail.assignedSites.length > 0 ? (
            <div className="space-y-4">
              {supervisorDetail.assignedSites.map((site) => (
                <div key={site.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-red-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          {site.siteId}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                          {site.typeSiteContract}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          site.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          site.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {site.status}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{site.siteName}</h3>

                      {/* Assignments with Scopes - ✅ Now shows SITAC and CME_IMPLEMENTATION */}
                      {site.assignments && site.assignments.length > 0 && (
                        <div className="mb-3 space-y-2">
                          <p className="text-sm font-semibold text-gray-700">Assigned Scopes:</p>
                          {site.assignments.map((assignment) => (
                            <div key={assignment.assignmentId} className="flex items-center gap-2 flex-wrap">
                              {assignment.scopes.map((scope) => {
                                const config = getScopeConfig(scope);
                                return (
                                  <div key={scope} className="group relative">
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
                                      <span>{config.icon}</span>
                                      <span>{config.label}</span>
                                      {assignment.scopes.length > 1 && (
                                        <button
                                          onClick={() => handleRemoveScope(assignment.assignmentId, scope, site.siteName)}
                                          className="ml-1 hover:bg-red-200 rounded-full p-0.5 transition-colors"
                                          title="Remove this scope"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                        <div>
                          <span className="text-gray-500">Project:</span>
                          <p className="font-medium text-gray-900">{site.projectName || '-'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Province:</span>
                          <p className="font-medium text-gray-900">{site.provinsi}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Kabupaten:</span>
                          <p className="font-medium text-gray-900">{site.kabupaten}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Tower Height:</span>
                          <p className="font-medium text-gray-900">{site.towerHeight}m</p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-semibold text-gray-900">{site.progressPercentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-red-600 to-red-500 h-2 rounded-full transition-all"
                            style={{ width: `${site.progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Unassign Button */}
                    {site.assignments && site.assignments.length > 0 && (
                      <button
                        onClick={() => handleUnassignById(site.assignments[0].assignmentId, site.siteName)}
                        className="ml-4 text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel Assignment"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No Tower Sites assigned yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <AssignmentModal
          supervisorId={supervisorId}
          supervisorName={supervisorDetail.fullName}
          onClose={() => setShowAssignmentModal(false)}
          onSuccess={handleAssignmentComplete}
        />
      )}
    </div>
  );
};

export default SupervisorDetailPage;